import type React from "react";
import { useState } from "react";
import Button from "../../../../../../core/button/Button";
import ColorPicker from "../../../../../../core/inputs/ColorPicker";
import Select from "../../../../../../core/select/Select";
import Seperator from "../../../../../../core/seperator/Seperator";
import LabelledSwitch from "../../../../../../core/switch/LabelledSwitch";
import type { ImageryStyle } from "../cesium-types";

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

interface SettingsPanelProps {
  onDepthTestChange: (enabled: boolean) => void;
  onShadowsChange: (enabled: boolean) => void;
  onImageryStyleChange: (style: ImageryStyle) => void;
  onPolygonDrawingChange: (enabled: boolean) => void;
  onOsmBuildingsChange: (enabled: boolean) => void;
  onOsmBuildingColorChange: (color: string, opacity: number) => void;
  imageryStyle: ImageryStyle;
  osmBuildingsEnabled: boolean;
  osmBuildingColor: string;
  conditions: StyleCondition[];
  onStyleConditionsChange: (conditions: StyleCondition[]) => void;
  osmBuildingOpacity: number;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  onDepthTestChange,
  onShadowsChange,
  onImageryStyleChange,
  onPolygonDrawingChange,
  onOsmBuildingsChange,
  onOsmBuildingColorChange,
  imageryStyle,
  osmBuildingsEnabled,
  osmBuildingColor,
  conditions,
  onStyleConditionsChange,
  osmBuildingOpacity,
}) => {
  const [depthTestEnabled, setDepthTestEnabled] = useState(false);
  const [shadowsEnabled, setShadowsEnabled] = useState(true);

  const handleDepthTestChange = (enabled: boolean) => {
    setDepthTestEnabled(enabled);
    onDepthTestChange(enabled);
  };

  const handleShadowsChange = (enabled: boolean) => {
    setShadowsEnabled(enabled);
    onShadowsChange(enabled);
  };

  const handleImageryStyleChange = (value: string | string[] | null) => {
    if (typeof value === "string") {
      onImageryStyleChange(value as ImageryStyle);
    }
  };

  const handleRemoveCondition = (index: number) => {
    const updatedConditions = conditions.filter((_, i) => i !== index);
    onStyleConditionsChange(updatedConditions);
  };

  const handleColorChange = (index: number, color: string, opacity: number) => {
    const updatedConditions = conditions.map((condition, i) => {
      if (i === index) {
        return { ...condition, color, opacity };
      }
      return condition;
    });
    onStyleConditionsChange(updatedConditions);
  };

  const handleToggleVisibility = (index: number) => {
    const condition = conditions[index];
    const newOpacity = condition.opacity === 0 ? 1.0 : 0;
    handleColorChange(index, condition.color, newOpacity);
  };

  return (
    <div className="fixed top-0 right-[544px] z-50 bg-surface-default border-r border-l border-border-default  h-screen p-6 max-w-sm overflow-y-auto">
      <h2 className="text-xl font-bold text-text-default mb-6">
        Cesium Settings
      </h2>

      <div className="space-y-4">
        <LabelledSwitch
          name="depthTest"
          label="Depth Test Against Terrain"
          toggled={depthTestEnabled}
          handleSetToggle={handleDepthTestChange}
        />

        <LabelledSwitch
          name="shadows"
          label="Shadows"
          toggled={shadowsEnabled}
          handleSetToggle={handleShadowsChange}
        />

        <div>
          <Select
            label="Imagery Style"
            data={[
              { value: "openstreetmap", text: "OpenStreetMap" },
              { value: "satellite", text: "Satellite" },
              { value: "satellite-buildings", text: "Satellite + Buildings" },
              { value: "photorealistic", text: "Photorealistic" },
              { value: "maptiler-streets", text: "MapTiler Streets" },
              { value: "maptiler-basic", text: "MapTiler Basic" },
              { value: "maptiler-bright", text: "MapTiler Bright" },
              { value: "maptiler-pastel", text: "MapTiler Pastel" },
              { value: "maptiler-topo", text: "MapTiler Topo" },
              { value: "maptiler-toner", text: "MapTiler Toner" },
              { value: "maptiler-hybrid", text: "MapTiler Hybrid" },
              { value: "maptiler-winter", text: "MapTiler Winter" },
              { value: "maptiler-positron", text: "MapTiler Positron" },
            ]}
            selected={imageryStyle}
            onValueChange={handleImageryStyleChange}
            size="md"
          />
        </div>
        <LabelledSwitch
          name="osmBuildings"
          label="OSM Buildings"
          toggled={osmBuildingsEnabled}
          handleSetToggle={onOsmBuildingsChange}
        />
        {osmBuildingsEnabled && (
            <ColorPicker
                initialColor={osmBuildingColor}
                initialOpacity={osmBuildingOpacity}
                handleSave={async (color, opacity) => {
                    onOsmBuildingColorChange(color, opacity);
                }}
            />
        )}

        <Button
          text="Draw Polygon"
          onClick={() => onPolygonDrawingChange(true)}
          className="w-full"
        />
      </div>

      {conditions.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-text-default mb-4">
            Style Conditions
          </h3>

          <div className="space-y-0">
            {conditions.map((condition, index) => (
              <div key={`${condition.property}-${condition.value}-${index}`}>
                <div className="relative py-4">
                  <div className="absolute top-2 right-0 flex gap-1">
                    <ColorPicker
                      initialColor={condition.color}
                      initialOpacity={condition.opacity}
                      // biome-ignore lint/suspicious/noAwaitInAsync: The handleSave prop requires a promise.
                      handleSave={async (color, opacity) => {
                        handleColorChange(index, color, opacity);
                      }}
                    />

                    <Button
                      variant="shapeless"
                      size="sm"
                      square
                      leadingIcon={
                        condition.opacity === 0 ? "eye-strikethrough" : "eye"
                      }
                      onClick={() => handleToggleVisibility(index)}
                      className=""
                    />
                    <Button
                      variant="shapeless"
                      size="sm"
                      square
                      leadingIcon="trash"
                      critical
                      onClick={() => handleRemoveCondition(index)}
                      className=""
                    />
                  </div>

                  <div className="space-y-3 pr-20">
                    <div className="">
                      <div className="body-sm font-medium text-text-subdued">
                        Property & Value
                      </div>
                      <p className="body-sm text-text-default mt-2">
                        {condition.property || "No property"}
                      </p>
                      <p className=" body-sm-bold text-text-default">
                        {condition.value || "No value"}
                      </p>
                    </div>
                  </div>
                </div>

                {index < conditions.length - 1 && <Seperator />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;
